import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Rnd } from "react-rnd";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { MdClose } from "react-icons/md";

import "./MainContent.css";
import { Button, ButtonGroup } from "reactstrap";
import nodeService from "../../services/nodeService";
import ActionModal from "../common/ActionModal";
import axios from "axios";
import api from "../../api/apiClient";
import NodeExportModal from "../common/NodeExportModal";
import { baseURL } from "../../env/env";

const SelectPane = ({
  isOpen,
  onClose,
  nodeOid,
  clickedNode,
  selectedNodes,
  setSelectedNodes,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState("restrict");
  const [apiData, setApiData] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [size, setSize] = useState({
    width: window.innerWidth * 0.38,
    height: parseInt(window.innerHeight) - window.innerHeight * 0.118,
  });
  const [position, setPosition] = useState({
    x: window.innerWidth * 0.225,
    y: 80,
  });

  const fetchData = async (oid) => {
    try {
      const response = await nodeService.fetchNodeData(oid);
      return response;
    } catch (error) {
      console.error("Error fetching data:", error);
      return [];
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const res = await api.get("/fetch/user");
      setUsers(res.data);
    };
    fetchUsers();
  }, [clickedNode]);

  useEffect(() => {
    if (nodeOid) {
      const fetchAndUpdateData = async () => {
        const data = await fetchData(nodeOid);
        if (data) {
          setSelectedNodes((prev) => [
            ...prev,
            { node: clickedNode, models: data },
          ]);
        }
      };
      fetchAndUpdateData();
    }
  }, [nodeOid, clickedNode]);

  const fetchInstances = async () => {
    try {
      const response = await axios.get(`${baseURL}/instances/`);
      setApiData(response.data);
    } catch (error) {
      console.error("Error fetching API data:", error);
    }
  };

  const toggleSelectAll = async () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      await fetchInstances();
    } else {
      setApiData([]);
    }
  };

  const togglePane = () => {
    if (isExpanded) {
      // If we're minimizing, reset the position to avoid issues when expanding again
      setPosition({ x: window.innerWidth * 0.225, y: 80 });
      setSize({ width: size.width, height: 40 });
    } else {
      // Reset to initial size and position when expanding
      setSize({
        width: window.innerWidth * 0.38,
        height: parseInt(window.innerHeight) - window.innerHeight * 0.118,
      });
      setPosition({ x: window.innerWidth * 0.225, y: 80 });
    }
    setIsExpanded(!isExpanded);
  };

  const clearSelection = () => {
    setSelectedNodes([]);
    setSelectAll(false);
    setApiData([]);
  };

  const handleClearAndClose = () => {
    clearSelection();
    onClose();
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleActionClick = (type) => {
    setActionType(type);
    setShowActionModal(true);
  };

  const handleCloseActionModal = () => {
    setSelectedRole("");
    setSelectedUser("");
    setShowActionModal(false);
  };

  const groupedApiData = apiData.reduce((acc, instance) => {
    const className = instance.class;
    if (!acc[className]) {
      acc[className] = [];
    }
    acc[className].push(instance.props.name);
    return acc;
  }, {});

  if (!isOpen) return null;

  const content = (
    <Rnd
      size={isExpanded ? size : { width: size.width, height: 40 }}
      position={position}
      onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
      onResize={(e, direction, ref, delta, position) => {
        setSize({
          width: ref.style.width,
          height: ref.style.height,
        });
        setPosition(position);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "white",
        boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
        border: "1px solid #ddd",
        zIndex: 1000,
        position: "fixed",
      }}
      bounds="window"
      dragHandleClassName="pane-header"
    >
      <div className="pane-header">
        <div className="selectclose-button">
          <MdClose onClick={handleClearAndClose} />
        </div>
        <div className="pane-title">Data Selection Panel</div>
        <div className="pane-controls">
          <button className="pane-button toggle-button" onClick={togglePane}>
            {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="pane-content">
          <div className="select-all-row">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectAll}
              onChange={toggleSelectAll}
            />
            <label htmlFor="selectAll" className="select-all-label">
              Select/Deselect All
            </label>
          </div>

          <div className="main-body">
            {selectAll
              ? Object.entries(groupedApiData).map(
                  ([className, names], index) => (
                    <div key={index}>
                      <div className="class-name">{className}</div>
                      <ul>
                        {names.map((name, nameIndex) => (
                          <li key={nameIndex}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  )
                )
              : selectedNodes.map((nodeData, index) => (
                  <div key={index}>
                    <div className="clicked-node-display">
                      {nodeData.node?.display}
                    </div>
                    <ul>
                      {nodeData.models.map((model, modelIndex) => (
                        <li key={modelIndex}>
                          {model.model_names} ({model.model_class})
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
          </div>

          <div className="select-footer">
            <div className="total-info">
              <span className="total-models">
                Total Models:{" "}
                {selectAll
                  ? new Set(apiData.map((instance) => instance.class)).size
                  : selectedNodes.length}
              </span>
              <span className="total-instances">
                Total Instances:{" "}
                {selectAll
                  ? apiData.length
                  : selectedNodes.length > 0
                  ? selectedNodes.reduce(
                      (total, node) => total + node.models.length,
                      0
                    )
                  : 0}
              </span>
            </div>
            <div className="button-group">
              <Button
                onClick={handleExportClick}
                disabled={selectedNodes.length === 0}
                style={{
                  cursor: selectedNodes.length === 0 ? "not-allowed" : "",
                }}
              >
                Export
              </Button>
              <Button
                onClick={() => handleActionClick("restrict")}
                disabled={selectAll || selectedNodes.length === 0}
                style={{
                  cursor: selectedNodes.length === 0 ? "not-allowed" : "",
                }}
              >
                Restrict
              </Button>
              <Button
                onClick={() => handleActionClick("allow")}
                disabled={selectAll || selectedNodes.length === 0}
                style={{
                  cursor: selectedNodes.length === 0 ? "not-allowed" : "",
                }}
              >
                Allow
              </Button>
              <Button onClick={clearSelection}>Clear</Button>
            </div>
          </div>
        </div>
      )}
      <NodeExportModal
        show={showExportModal}
        setShowExportModal={setShowExportModal}
        selectedNodes={selectedNodes}
      />
      <ActionModal
        show={showActionModal}
        handleClose={handleCloseActionModal}
        actionType={actionType}
        users={users}
        selectedNodes={selectedNodes}
        selectedRole={selectedRole}
        selectedUser={selectedUser}
        setSelectedRole={setSelectedRole}
        setSelectedUser={setSelectedUser}
      />
    </Rnd>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default SelectPane;
